import React from "react";
import { Globe, Coins, Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";

/**
 * Inline language & currency selectors.
 *
 * Same global context as before (LanguageContext, CurrencyContext) so
 * changing one selector instantly updates every other selector on the
 * page AND every consumer (booking widget, sticky summary, room rates,
 * spa menu, etc.). Choices are persisted to localStorage by the
 * providers, so they survive navigation + full refreshes.
 *
 * Two visual treatments are exposed via `variant`:
 *   - "light" → for cream / white surfaces (e.g. the side-nav drawer)
 *   - "dark"  → for the inky footer
 *
 * Both selectors render a single pill-row (no popover) so they're
 * obvious and tap-friendly on every viewport without obscuring page
 * content.
 */

const DISCLAIMER = {
  en: "Indicative rates — invoices issued in MAD.",
  fr: "Tarifs indicatifs — factures émises en MAD.",
  es: "Precios orientativos. El importe final de la reserva se confirma en dirhams marroquíes (MAD).",
};

const LABEL = {
  language: { en: "Language", fr: "Langue", es: "Idioma" },
  currency: { en: "Currency", fr: "Devise", es: "Moneda" },
};

const tone = (variant) => {
  if (variant === "dark") {
    return {
      group: "text-[#FDFBF7]",
      label: "text-[#D4A373]",
      pill: "border-[#FDFBF7]/15 text-[#FDFBF7]/85 hover:border-[#D4A373] hover:text-[#D4A373]",
      active: "bg-[#D4A373] text-[#1A1513] border-[#D4A373]",
      icon: "text-[#D4A373]",
      disclaimer: "text-[#FDFBF7]/55",
    };
  }
  return {
    group: "text-[#2C2621]",
    label: "text-[#5C5248]",
    pill: "border-[#2C2621]/15 text-[#2C2621] hover:border-[#C16542] hover:text-[#C16542]",
    active: "bg-[#C16542] text-[#FDFBF7] border-[#C16542]",
    icon: "text-[#C16542]",
    disclaimer: "text-[#5C5248]/80",
  };
};

export const InlineLanguageSelector = ({ variant = "light", testIdPrefix = "lang-selector" }) => {
  const { lang, setLang, languages } = useLanguage();
  const t = tone(variant);
  return (
    <div className={`flex flex-col gap-2 ${t.group}`} data-testid={testIdPrefix}>
      <div className="inline-flex items-center gap-2">
        <Globe className={`w-3.5 h-3.5 ${t.icon}`} strokeWidth={1.6} />
        <span className={`text-[10px] tracking-[0.3em] uppercase ${t.label}`}>
          {LABEL.language[lang] || LABEL.language.en}
        </span>
      </div>
      <div role="group" aria-label="Language" className="inline-flex flex-wrap items-center gap-1.5">
        {languages.map((l) => {
          const active = lang === l.code;
          return (
            <button
              key={l.code}
              type="button"
              onClick={() => setLang(l.code)}
              aria-pressed={active}
              className={`inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-[10px] tracking-[0.3em] uppercase transition-colors ${
                active ? t.active : t.pill
              }`}
              data-testid={`${testIdPrefix}-${l.code}`}
            >
              {active && <Check className="w-3 h-3" strokeWidth={2.2} />}
              {l.code.toUpperCase()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const InlineCurrencySelector = ({
  variant = "light",
  testIdPrefix = "currency-selector",
  showDisclaimer = true,
}) => {
  const { currency, setCurrency, currencies } = useCurrency();
  const { lang } = useLanguage();
  const t = tone(variant);
  return (
    <div className={`flex flex-col gap-2 ${t.group}`} data-testid={testIdPrefix}>
      <div className="inline-flex items-center gap-2">
        <Coins className={`w-3.5 h-3.5 ${t.icon}`} strokeWidth={1.6} />
        <span className={`text-[10px] tracking-[0.3em] uppercase ${t.label}`}>
          {LABEL.currency[lang] || LABEL.currency.en}
        </span>
      </div>
      <div role="group" aria-label="Currency" className="inline-flex flex-wrap items-center gap-1.5">
        {currencies.map((c) => {
          const active = currency === c.code;
          return (
            <button
              key={c.code}
              type="button"
              onClick={() => setCurrency(c.code)}
              aria-pressed={active}
              className={`inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-[10px] tracking-[0.3em] uppercase transition-colors ${
                active ? t.active : t.pill
              }`}
              data-testid={`${testIdPrefix}-${c.code}`}
            >
              {active && <Check className="w-3 h-3" strokeWidth={2.2} />}
              {c.code}
            </button>
          );
        })}
      </div>
      {showDisclaimer ? (
        <p
          className={`text-[10px] leading-snug italic max-w-[18rem] ${t.disclaimer}`}
          data-testid={`${testIdPrefix}-disclaimer`}
        >
          {DISCLAIMER[lang] || DISCLAIMER.en}
        </p>
      ) : null}
    </div>
  );
};

/**
 * Convenience wrapper rendering both selectors side-by-side. Used by
 * both the footer and the side-nav drawer so behaviour stays
 * consistent across surfaces.
 */
const SitePreferences = ({ variant = "light", testIdPrefix = "site-preferences" }) => (
  <div
    className="grid grid-cols-1 sm:grid-cols-2 gap-6"
    data-testid={testIdPrefix}
  >
    <InlineLanguageSelector
      variant={variant}
      testIdPrefix={`${testIdPrefix}-lang`}
    />
    <InlineCurrencySelector
      variant={variant}
      testIdPrefix={`${testIdPrefix}-currency`}
    />
  </div>
);

export default SitePreferences;
